/*
  ゆめさくの動きの決まり (~/.claude/skills/yumesaku-motion-system/SKILL.md) の値の表。
  LP の動きはここの値だけを使う。CSS には motionCssVars() で変数として渡し、
  motion (framer-motion) には同じ定数をそのまま渡す。表に無い長さやイージングを各所で書かない
*/

// イージング。out は入る・数字・CTA、in は出る
type Bezier = [number, number, number, number];
export const EASE_OUT: Bezier = [0.2, 0.7, 0.2, 1];
export const EASE_IN: Bezier = [0.4, 0, 1, 1];
export const EASE_OUT_CSS = "cubic-bezier(0.2, 0.7, 0.2, 1)";
export const EASE_IN_CSS = "cubic-bezier(0.4, 0, 1, 1)";

// 長さ (ms)
export const DUR = {
  enter: 500,
  exit: 250,
  heroAssembly: 900,
  stagger: 70,
  countUp: 1200,
  cta: 150,
  // reduced motion で残してよい opacity だけの切り替えの上限
  reducedFade: 150,
} as const;

// 時差をつける数の上限。これを超える並びは最後の時差にまとめる
export const STAGGER_MAX = 5;
export const STAGGER_MAX_MOBILE = 3;

// 入る・出るの移動量 (px) と、カード・画面の出だしの大きさ
export const ENTER_Y = 24;
export const ENTER_Y_MOBILE = 16;
export const EXIT_Y = -8;
export const CARD_SCALE_FROM = 0.97;

// カード・画面のばね。行き過ぎがほぼ出ない値
export const SPRING = { type: "spring", stiffness: 260, damping: 30, mass: 1 } as const;

/*
  CSS の @keyframes でも同じばねを使えるよう、SPRING を数値で解いて linear() にする。
  落ち着くまでの時間がそのまま長さになる (260/30/1 でおよそ 470ms)
*/
function solveSpring(): { easing: string; ms: number } {
  const { stiffness: k, damping: c, mass: m } = SPRING;
  const dt = 1 / 600;
  let x = 0;
  let v = 0;
  let step = 0;
  const points: number[] = [0];
  for (;;) {
    const a = (-k * (x - 1) - c * v) / m;
    v += a * dt;
    x += v * dt;
    step += 1;
    if (step % 10 === 0) points.push(x);
    if (Math.abs(x - 1) < 0.002 && Math.abs(v) < 0.02) break;
  }
  points.push(1);
  return { easing: `linear(${points.map((p) => p.toFixed(3)).join(",")})`, ms: Math.round((step * dt) * 1000) };
}
export const SPRING_CSS = solveSpring();

// スクロール連動。scrub 1.0 はスクロール量と進みを 1 対 1 で結ぶ (linear)
export const SCRUB = 1.0;
// 固定して段を進めるとき、1段に使う画面の高さ (vh)。表の 80〜100% の中から
export const PIN_STEP_VH = 90;

// CTA の hover で矢印を右へ送る量 (px)
export const CTA_ARROW_X = 4;

/*
  Hero の物語の台本。表の「Hero の5〜10秒の物語」の1本だけがループする。
  at は1周の中で、その段が入り始める時刻 (ms)。入る動き自体の長さは DUR.enter
*/
export const HERO_LOOP_MS = 10000;
export const HERO_BEATS = {
  review: 300,
  fix: 1500,
  approved: 2200,
  filter: 3400,
  ship: 4800,
  // 全部の札が出る直前の時刻。ここから DUR.exit で消えて、次の周へ戻る
  reset: HERO_LOOP_MS - DUR.exit,
} as const;

// motion (framer-motion) へ渡す形。長さは秒
export const T_ENTER = { duration: DUR.enter / 1000, ease: EASE_OUT };
export const T_COUNT = { duration: DUR.countUp / 1000, ease: EASE_OUT };

export function staggerDelay(index: number, mobile = false): number {
  const max = mobile ? STAGGER_MAX_MOBILE : STAGGER_MAX;
  return Math.min(index, max - 1) * DUR.stagger;
}

// CSS の変数にして渡す。CSS の中では var(--m-enter) のように読む
export function motionCssVars(): string {
  return [
    `--m-enter:${DUR.enter}ms`,
    `--m-exit:${DUR.exit}ms`,
    `--m-cta:${DUR.cta}ms`,
    `--m-fade:${DUR.reducedFade}ms`,
    `--m-stagger:${DUR.stagger}ms`,
    `--m-out:${EASE_OUT_CSS}`,
    `--m-in:${EASE_IN_CSS}`,
    // スマホが先。PC の幅では CSS で --m-enter-y を --m-enter-y-pc に置き換える
    `--m-enter-y:${ENTER_Y_MOBILE}px`,
    `--m-enter-y-pc:${ENTER_Y}px`,
    `--m-exit-y:${EXIT_Y}px`,
    `--m-arrow-x:${CTA_ARROW_X}px`,
    `--m-loop:${HERO_LOOP_MS}ms`,
    `--m-spring:${SPRING_CSS.easing}`,
    `--m-spring-ms:${SPRING_CSS.ms}ms`,
  ].join(";");
}

/*
  Hero のループの1要素ぶんの @keyframes を作る。at で入り、reset で出て、次の周の頭は見えない。
  kind が "card" なら表の「カード・画面」(scale とばね)、"enter" なら「入る」(y と out)。
  移動量は px で渡す。@keyframes の中で var() を使うと、合成 (compositor) で動かず毎フレーム主スレッドで計算し直すため
  outAt を渡すと、その時刻に出る (アドレスバーの URL の入れ替え用)
*/
export function heroKeyframes(
  name: string,
  at: number,
  kind: "enter" | "card",
  outAt: number = HERO_BEATS.reset,
  enterY: number = ENTER_Y
): string {
  const pct = (ms: number) => `${((ms / HERO_LOOP_MS) * 100).toFixed(2)}%`;
  const inMs = kind === "card" ? SPRING_CSS.ms : DUR.enter;
  const hidden = kind === "card" ? `opacity:0;transform:scale(${CARD_SCALE_FROM})` : `opacity:0;transform:translateY(${enterY}px)`;
  const ease = kind === "card" ? SPRING_CSS.easing : EASE_OUT_CSS;
  const outEnd = Math.min(outAt + DUR.exit, HERO_LOOP_MS);
  return `@keyframes ${name}{
0%,${pct(at)}{${hidden};animation-timing-function:${ease}}
${pct(at + inMs)},${pct(outAt)}{opacity:1;transform:none;animation-timing-function:${EASE_IN_CSS}}
${pct(outEnd)},100%{opacity:0;transform:translateY(${EXIT_Y}px)}
}`;
}

/* 最初から見えていて、outAt で出て、次の周の頭でまた見える要素 (ローカルの URL) */
export function heroKeyframesUntil(name: string, outAt: number, enterY: number = ENTER_Y): string {
  const pct = (ms: number) => `${((ms / HERO_LOOP_MS) * 100).toFixed(2)}%`;
  return `@keyframes ${name}{
0%,${pct(outAt)}{opacity:1;transform:none;animation-timing-function:${EASE_IN_CSS}}
${pct(outAt + DUR.exit)}{opacity:0;transform:translateY(${EXIT_Y}px)}
${pct(HERO_LOOP_MS - DUR.enter)}{opacity:0;transform:translateY(${enterY}px);animation-timing-function:${EASE_OUT_CSS}}
100%{opacity:1;transform:none}
}`;
}

/*
  開発演習の LP (/women/lp/prod-dev) だけの動き「本番に出す」。差分の行が入り、検査の印が順に通り、本番に切り替わる。
  HP の「方眼に組む・積み上げる・四角が埋まる」とは別の文法。値はこの表の Enter、ばね、時差から組む。
  SHIP_STEP_MS は、印が1つ通ってから次の印が通るまでの間隔。時差の幅を4つぶん空けて、1つずつ目で追えるようにする。
  時差の上限 (5個) を超える並び (工程の6つ) は時差ではなく「順番」の役なので、この間隔で全部を順に通す
*/
export const SHIP_STEP_MS = DUR.stagger * 4;
// 差分の行の地 (薄い青) が引かれてから消えるまでの間。読める長さだけ残して、最後の形は地なしに戻す
export const SHIP_HOLD_MS = DUR.countUp;
